import Papa from 'papaparse';

/**
 * Reduces a header to letters and digits only, so matching tolerates the
 * template's casing, spaces, the required-field `*`, `#`, `.`, and any BOM
 * (e.g. "FMS ID*", "Unit #*", "VIN*").
 */
export const normalizeHeader = (header: string): string =>
  header.toLowerCase().replace(/[^a-z0-9]/g, '');

export type HeaderMismatch = {
  missing: string[];
  unexpected: string[];
};

/**
 * Compares a file's header row against the columns of the current template.
 *
 * Both an absent template column and an unrecognised extra column are
 * rejected: an uploader working from a stale template would otherwise pass
 * silently, which is the case QA hit on PD-4658. Order does not matter, and a
 * trailing empty header cell is ignored because spreadsheet exports routinely
 * leave one behind.
 *
 * Returns the offending column names so the UI can name them, rather than
 * leaving the user to diff two header rows by eye.
 */
export const findHeaderMismatch = (
  fields: string[],
  expected: string[],
): HeaderMismatch => {
  const present = fields
    .filter((field) => normalizeHeader(field) !== '')
    .map(normalizeHeader);

  const missing = expected.filter(
    (column) => !present.includes(normalizeHeader(column)),
  );

  const expectedSet = expected.map(normalizeHeader);
  const unexpected = fields.filter((field) => {
    const normalized = normalizeHeader(field);
    return normalized !== '' && !expectedSet.includes(normalized);
  });

  return { missing, unexpected };
};

export const hasHeaderMismatch = (mismatch: HeaderMismatch): boolean =>
  mismatch.missing.length > 0 || mismatch.unexpected.length > 0;

/**
 * Reads only the header row, so a wrong file is rejected without parsing
 * every data row.
 */
export const readCsvHeaders = (file: File): Promise<string[]> =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 1,
      complete: (result) => resolve(result.meta.fields ?? []),
      error: (error) =>
        reject(new Error(`Failed to parse CSV: ${error.message}`)),
    });
  });
